/*
  # News Posts Admin List Component
  
  1. Changes
    - Created admin interface for managing news posts
    - Added drag-and-drop reordering functionality
    - Integrated with database for CRUD operations
*/

import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, GripVertical, Calendar, Eye, EyeOff } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import { NewsPost } from '../../types/news';
import NewsPostModal from './NewsPostModal';
import { formatDate } from '../../utils/dateUtils';

interface SortableItemProps {
  post: NewsPost;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePublished: () => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ post, index, onEdit, onDelete, onTogglePublished }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: post.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group p-6 bg-[#1F2B1F] rounded-xl"
    >
      <div className="flex items-start gap-4">
        <div {...attributes} {...listeners} className="cursor-grab mt-2">
          <GripVertical className="w-5 h-5 text-white/40" />
        </div>
        
        <div className="aspect-video w-32 overflow-hidden rounded-lg flex-shrink-0">
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-2 line-clamp-2">{post.title}</h3>
              <p className="text-sm text-white/70 mb-2 line-clamp-2">{post.excerpt}</p>
              <div className="flex flex-wrap items-center gap-4 text-sm text-white/70 mb-2">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(new Date(post.published_date), { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    post.is_published 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {post.is_published ? 'Published' : 'Draft'}
                  </span>
                  <span className="text-xs text-white/40">Position: {index + 1}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={onTogglePublished}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                title={post.is_published ? 'Unpublish' : 'Publish'}
              >
                {post.is_published ? (
                  <EyeOff className="w-4 h-4 text-yellow-500" />
                ) : (
                  <Eye className="w-4 h-4 text-green-500" />
                )}
              </button>
              <button
                onClick={onEdit}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={onDelete}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-red-500"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NewsPostsList = () => {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<NewsPost | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('news_posts')
        .select('*')
        .order('display_order', { ascending: true })
        .order('published_date', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = posts.findIndex((p) => p.id === active.id);
    const newIndex = posts.findIndex((p) => p.id === over.id);
    
    const newOrder = arrayMove(posts, oldIndex, newIndex);

    try {
      // Update display orders in database
      for (let i = 0; i < newOrder.length; i++) {
        const { error } = await supabase
          .from('news_posts')
          .update({ display_order: i })
          .eq('id', newOrder[i].id);

        if (error) throw error;
      }

      setPosts(newOrder);
    } catch (err) {
      console.error('Error updating post order:', err);
      fetchPosts();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const { error } = await supabase
        .from('news_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setPosts(posts.filter(post => post.id !== id));
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Failed to delete post');
    }
  };

  const handleTogglePublished = async (post: NewsPost) => {
    try {
      const { error } = await supabase
        .from('news_posts')
        .update({ is_published: !post.is_published })
        .eq('id', post.id);

      if (error) throw error;
      
      setPosts(posts.map(p => 
        p.id === post.id 
          ? { ...p, is_published: !p.is_published }
          : p
      ));
    } catch (err) {
      console.error('Error updating post:', err);
      alert('Failed to update post');
    }
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">News & Updates</h2>
        <button
          onClick={() => {
            setSelectedPost(null);
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Post
        </button>
      </div>

      <div className="bg-[#1F2B1F] rounded-xl overflow-hidden">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={posts}
            strategy={verticalListSortingStrategy}
          >
            <div className="divide-y divide-white/10">
              {posts.length === 0 ? (
                <div className="p-8 text-center text-white/50">No posts found</div>
              ) : (
                <div className="space-y-4 p-4">
                  {posts.map((post, index) => (
                    <SortableItem
                      key={post.id}
                      post={post}
                      index={index}
                      onEdit={() => {
                        setSelectedPost(post);
                        setIsModalOpen(true);
                      }}
                      onDelete={() => handleDelete(post.id)}
                      onTogglePublished={() => handleTogglePublished(post)}
                    />
                  ))}
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <NewsPostModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPost(null);
        }}
        post={selectedPost}
        onSave={fetchPosts}
      />
    </div>
  );
};

export default NewsPostsList;